import React, { CSSProperties } from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps } from 'react-router'
import { withRouter } from 'react-router-dom'
import { ContextRoute } from './app/context'
import { Operation } from './domain/Operation'
import {
    DifferentialFlamegraphNode,
    diffFlamegraphs,
    SimpleTrace,
    Trace,
    TraceDiffItem,
    traceHasCallgraphData,
    traceSpanCategories,
} from './domain/Trace'
import { TraceDiffTranslation, traceIsOlder, TraceSummarySingleView } from './domain/TraceSummary'
import Link from './helper/link'
import Loading from './helper/loading'
import { ApplicationState, isFeatureEnabled } from './redux/ApplicationState'
import { fetchTrace } from './redux/V2/Trace/actions'
import { getTrace, isTraceLoaded } from './redux/V2/Trace/TraceState'
import Router from './router'
import { Callgraph } from './trace/callgraph'
import TraceOverview from './trace/overview'
import { Container } from './ui/grid'
import { xhprofCompactGraph } from './xhprof/XhprofCompactGraph'
import { compareXhprofRawData } from './xhprof/XhprofCompareRawData'
import { generateXhprofReport } from './xhprof/XhprofReportGenerator'
import { PortalContentNavigationPublic } from './helper/portal-content-navigation-public'
import PublicDiff from './trace/diff/PublicDiff'
import { FlamegraphComponent } from './trace/flamegraph'
import { parseQueryString } from './helper/queryString'

interface MatchParameters {
    shareId: string
    baseShareId: string
}

interface OwnProps {
    view?: 'callgraph' | 'timeline' | 'flamegraph' | 'summary'
    traceId: string
    baseTraceId: string
}

interface DispatchProps {
    fetchTrace: (id: string) => void
}

interface StateProps {
    parameters: { [key: string]: string }
    router: Router
    route: ContextRoute
    traceBase: SimpleTrace | null
    traceTarget: SimpleTrace | null
    traceBaseId: string
    traceTargetId: string
    shareId: string
    baseShareId: string
    traceBaseLoaded: boolean
    traceTargetLoaded: boolean
    sidebarMenuEnabled: boolean
}

type Props = OwnProps & StateProps & DispatchProps & RouteComponentProps<MatchParameters>

interface State {
    baseProfile: TraceSummarySingleView | null
    baseOldProfile: Trace | null
    targetProfile: TraceSummarySingleView | null
    targetOldProfile: Trace | null
    hasCallgraph: boolean
    operation?: Operation
    diff?: TraceDiffItem[]
}

export class PublicTraceDiff extends React.Component<Props, State> {
    public state: State = {
        baseProfile: null,
        baseOldProfile: null,
        targetProfile: null,
        targetOldProfile: null,
        hasCallgraph: false,
    }

    public componentDidMount() {
        this.props.router.setDefaultParameter('shareId', this.props.parameters.shareId)
        this.props.router.setDefaultParameter('baseShareId', this.props.parameters.baseShareId)
        this.reload()
    }

    public componentDidUpdate(prevProps) {
        if (
            prevProps.parameters.shareId !== this.props.parameters.shareId ||
            prevProps.parameters.baseShareId !== this.props.parameters.baseShareId
        ) {
            this.props.router.setDefaultParameter('shareId', this.props.parameters.shareId)
            this.props.router.setDefaultParameter('baseShareId', this.props.parameters.baseShareId)
            this.reload()
        }
    }

    public reload = () => {
        jQuery.ajax({
            url: this.props.router.path('XhprofCoreBundle.Public.sharedTrace.compareData', this.props.parameters),
            success: data => {
                this.setState(data)
            },
        })
    }

    public render() {
        const active = this.props.view

        if (!this.state.baseProfile || !this.state.targetProfile) {
            return <Loading />
        }

        let diffTrace: Trace | null = null
        let compareCallgraphs = false
        let diffFlamegraph: DifferentialFlamegraphNode | null = null

        if (this.props.traceTargetLoaded && this.props.traceBaseLoaded && this.props.traceBase && this.props.traceTarget) {
            const diffCompareRawData = compareXhprofRawData(this.props.traceBase.rawData, this.props.traceTarget.rawData)
            if (this.state.baseOldProfile?.flamegraph && this.state.targetOldProfile?.flamegraph) {
                diffFlamegraph = diffFlamegraphs(this.state.targetOldProfile.flamegraph, this.state.baseOldProfile.flamegraph)
            }

            compareCallgraphs = traceHasCallgraphData(this.props.traceBase) && traceHasCallgraphData(this.props.traceTarget)

            const reportDiff = generateXhprofReport(diffCompareRawData)
            const reportCompact = compareCallgraphs ? { ...reportDiff, data: xhprofCompactGraph(reportDiff.data, 95) } : null

            diffTrace = {
                id: 'diff-trace',
                traceId: 1234,
                time: 1234,
                transaction: '',
                rawData: diffCompareRawData,
                xhprofReport: reportDiff,
                xhprofReportCompact: reportCompact,
                spans: [],
                globalEvents: [],
                ticks: {},
                hidden: 0,
                memoryLimit: false,
                parentSpanId: null,
                server: this.props.traceBase.server,
                service: this.props.traceBase.service,
                environment: this.props.traceBase.environment,
                measureDate: this.props.traceBase.measureDate,
                flamegraph: {
                    id: '1',
                    name: 'root',
                    title: 'root',
                    values: { mc: 0, wt: 0 },
                    children: [],
                    cat: 'app',
                    customType: null,
                    stats: { ct: 1, mc: 0, wt: 0 },
                    stack: null,
                },
            }
        }

        let compareButtonPosition: CSSProperties = { position: 'absolute', marginLeft: -80, marginTop: -100 }
        if (this.props.sidebarMenuEnabled) {
            compareButtonPosition = { position: 'absolute', marginLeft: 20, marginTop: -100 }
        }

        const traceDiffTranslation: TraceDiffTranslation = {
            from: traceIsOlder(this.state.baseProfile, this.state.targetProfile) ? 'Before' : 'After',
            to: traceIsOlder(this.state.targetProfile, this.state.baseProfile) ? 'Before' : 'After',
        }

        return (
            <div>
                <PortalContentNavigationPublic>
                    <div className="clearfix" style={{ background: 'white' }}>
                        <Container>
                            <div className={this.props.sidebarMenuEnabled ? 'tw-pl-32' : ''}>
                                <TraceOverview
                                    prefix="Base: "
                                    profile={this.state.baseProfile}
                                    operation={this.state.operation}
                                    isSharedTraceView={true}
                                />
                                <TraceOverview
                                    prefix="Target: "
                                    profile={this.state.targetProfile}
                                    operation={this.state.operation}
                                    isSharedTraceView={true}
                                />
                            </div>

                            <div style={compareButtonPosition}>
                                <Link
                                    className="btn btn-default"
                                    route="XhprofCoreBundle.Public.sharedTrace.compare"
                                    params={{ baseShareId: this.props.shareId, shareId: this.props.baseShareId }}
                                >
                                    <span className="fa fa-exchange" />
                                </Link>
                            </div>
                        </Container>
                    </div>
                </PortalContentNavigationPublic>
                <div className="row">
                    <div className="col-lg-12 col-md-12">
                        <ul className="nav nav-pills nav-pills-tideways">
                            <li className={active === 'flamegraph' ? 'active' : ''}>
                                <Link route="XhprofCoreBundle.Public.sharedTrace.compare">Flamegraph</Link>
                            </li>
                            <li className={active === 'timeline' || active === 'summary' ? 'active' : ''}>
                                <Link route="XhprofCoreBundle.Public.sharedTrace.summaryCompare">Summary</Link>
                            </li>
                            {compareCallgraphs && diffTrace ? (
                                <li className={active === 'callgraph' ? 'active' : ''}>
                                    <Link route="XhprofCoreBundle.Public.sharedTrace.callgraphCompare">Callgraph</Link>
                                </li>
                            ) : null}
                        </ul>
                    </div>
                </div>
                {(() => {
                    const queryParameters = parseQueryString(this.props.location.search)
                    switch (active) {
                        case 'timeline':
                        case 'summary':
                            return (
                                <PublicDiff
                                    diff={this.state.diff}
                                    base={this.state.baseProfile}
                                    target={this.state.targetProfile}
                                    traceDiffTranslation={traceDiffTranslation}
                                />
                            )
                        case 'flamegraph':
                            return this.state.baseOldProfile && diffFlamegraph ? (
                                <FlamegraphComponent
                                    route="XhprofCoreBundle.Public.sharedTrace.flamegraphCompare"
                                    spans={this.state.baseOldProfile.spans}
                                    data={diffFlamegraph}
                                    categories={traceSpanCategories}
                                    router={this.props.router}
                                    params={queryParameters}
                                    isAggregatedTrace={false}
                                    differentialMode
                                    traceDiffTranslation={traceDiffTranslation}
                                    layers={{ base: this.state.baseProfile.layers, target: this.state.targetProfile.layers }}
                                    switchComparisonOrder={() => {
                                        this.props.router.push(this.props.route.route, {
                                            ...this.props.parameters,
                                            profile: this.props.traceBaseId,
                                            base: this.props.traceTargetId,
                                        })
                                    }}
                                />
                            ) : null
                        case 'callgraph':
                            return diffTrace ? (
                                <Callgraph
                                    startTracepointEvent={null}
                                    trace={diffTrace}
                                    router={this.props.router}
                                    url={''}
                                    clickRoute="XhprofCoreBundle.Public.sharedTrace.callgraphCompare"
                                    params={this.props.parameters}
                                    showPlusWhenPositive
                                    includesDynamicallyInstrumentedFunctions={false}
                                    sidebarMenuEnabled={this.props.sidebarMenuEnabled}
                                    bottlenecks={[]}
                                />
                            ) : null
                    }
                })()}
            </div>
        )
    }
}

export default withRouter(
    connect<StateProps, DispatchProps, OwnProps>(
        (globalState: ApplicationState, props: RouteComponentProps<MatchParameters> & DispatchProps & OwnProps) => {
            const targetTraceId = props.baseTraceId
            const baseTraceId = props.traceId
            const shareId = props.match.params.shareId
            const baseShareId = props.match.params.baseShareId

            return {
                ...props,
                parameters: globalState.tidewaysContext.route.parameters,
                router: globalState.tidewaysContext.router,
                route: globalState.tidewaysContext.route,
                traceBase: getTrace(globalState, targetTraceId),
                traceTarget: getTrace(globalState, baseTraceId),
                traceBaseId: targetTraceId,
                traceTargetId: baseTraceId,
                sidebarMenuEnabled: isFeatureEnabled(globalState, 'sidebar_menu'),
                traceTargetLoaded: isTraceLoaded(globalState, baseTraceId),
                traceBaseLoaded: isTraceLoaded(globalState, targetTraceId),
                shareId,
                baseShareId,
            }
        },
        { fetchTrace },
    )(PublicTraceDiff),
)
